/* ============================================================================
   M6 COD Reconciliation — shared logic + mutable in-memory state.
   Frontend-only React port of the mockup js/m6.js. Mirrors the D+N cycle model,
   Expected Remittance Date computation, COD Status logic, RBAC vertical
   segregation (§4.3), saved column mappings and upload history.
   Single ProShip-admin role assumed (mockup PS.ROLE); the module is scoped to
   the D2C vertical only — D2C data is visible and commercially actionable.
   ========================================================================== */
import {
  DUMMY_COD_ROWS,
  DUMMY_COD_CONFIG,
  DUMMY_DISPUTES,
  DUMMY_CODES,
  DUMMY_AGREEMENTS,
  getPartner,
  TODAY,
} from '../dummyData'

/* re-export shared dummy-data symbols the tab files pull from this barrel */
export {DUMMY_CODES, TODAY} from '../dummyData'

/* ───────── constants ───────── */
export const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
export const COD_STATUS_OPTS = ['Received', 'Due', 'Overdue', 'Disputed', 'Reversed']
export const MISMATCH_TYPES = ['Delivered ↔ RTO', 'Delivered ↔ Lost', 'Short-paid', 'Reversal', 'Other']
export const DISPUTE_LIFECYCLE = [
  'Open',
  'Sent to Ops',
  'Status Corrected',
  'Reversal Generated',
  'Resolved',
  'Rejected',
]
export const CURRENT_USER = 'ashwani.kumar@prozo.com'
export const AGREEMENTS = DUMMY_AGREEMENTS

/* canonical remittance MIS columns (M6.5.2) */
export const CANON_COLS = [
  {k: 'AWB_Number', req: true, m: 'Shipment number; match key against the grid'},
  {k: 'Order_Number', req: false, m: 'Merchant order reference'},
  {k: 'Courier_Name', req: false, m: 'Courier the remittance is from'},
  {k: 'Delivery_Date', req: false, m: 'Delivery date of shipment'},
  {k: 'COD_Amount', req: true, m: 'COD value expected from courier'},
  {k: 'Remitted_Amount', req: true, m: 'Amount courier actually remitted'},
  {k: 'Transaction_Date', req: true, m: 'Date of remittance transaction'},
  {k: 'UTR_Number', req: true, m: 'Bank transaction reference'},
  {k: 'Payment_Mode', req: false, m: 'ONLINE / NEFT / RTGS / other'},
  {k: 'Bank_Detail', req: false, m: 'Remitting bank'},
  {k: 'Remark', req: false, m: 'Free-text note'},
]

/* per-courier saved column mappings (M6.5.2) — seed Delhivery so "reuse" is demoable */
export const SAVED_MAPPINGS = {
  Delhivery_ENT_SURFACE: {
    AWB_Number: 'Waybill',
    Order_Number: 'Ref No',
    Courier_Name: 'Carrier',
    Delivery_Date: 'DeliveredOn',
    COD_Amount: 'COD',
    Remitted_Amount: 'AmountPaid',
    Transaction_Date: 'TxnDate',
    UTR_Number: 'UTR',
    Payment_Mode: 'Mode',
    Bank_Detail: 'Bank',
    Remark: 'Notes',
  },
}

/* mutable, in-memory module state (local — no Redux, persists for the session) */
export const COD_ROWS = DUMMY_COD_ROWS.map((r) => ({...r}))
export const COD_CONFIG = DUMMY_COD_CONFIG.map((c) => ({...c, weekdays: c.weekdays.slice()}))
export const DISPUTES = DUMMY_DISPUTES.map((d) => ({...d}))
export const UPLOAD_HISTORY = [
  {
    by: CURRENT_USER,
    at: '2026-05-28T11:20:00',
    courier: 'Delhivery',
    cpCode: 'Delhivery_ENT_SURFACE',
    vertical: 'D2C',
    file: 'delhivery_cod_may_w4.xlsx',
    remark: 'Weekly settlement, week 4',
    matched: 812,
    unmatched: 14,
    shortpaid: 9,
    reversals: 6,
    duplicates: 2,
  },
  {
    by: CURRENT_USER,
    at: '2026-05-21T16:05:00',
    courier: 'Xpressbees',
    cpCode: 'Xpressbees_PTL',
    vertical: 'PTL',
    file: 'xb_ptl_remit_0521.csv',
    remark: 'PTL fortnightly',
    matched: 318,
    unmatched: 5,
    shortpaid: 3,
    reversals: 1,
    duplicates: 0,
  },
]

/* ───────── RBAC §4.3 (single admin role; module scoped to D2C only) ───────── */
export const visibleVerticals = () => ['D2C']
export const hasCommercial = () => true
export const canAct = () => true
export const codeVertical = (cpCode) => {
  const c = DUMMY_CODES.find((x) => x.code === cpCode)
  return c ? c.vertical : null
}
export const disputeVertical = (d) => codeVertical(d.cpCode) || 'D2C'

/* ───────── remittance MIS parser (M6.18) ───────── */
export function parseRemittance(text) {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((l) => l.replace(/\s+$/, ''))
    .filter((l) => l.trim())
  if (lines.length < 2) return null
  const delim = /\t/.test(lines[0]) ? '\t' : /;/.test(lines[0]) ? ';' : ','
  const headers = lines[0].split(delim).map((h) => h.trim())
  if (headers.length < 2) return null
  const rows = lines.slice(1).map((ln) => {
    const cells = ln.split(delim)
    const o = {}
    headers.forEach((h, i) => {
      o[h] = (cells[i] || '').trim()
    })
    return o
  })
  return {headers, rows}
}

/* ───────── M6.1.2 Expected Remittance Date computation ───────── */
function configFor(row) {
  const del = new Date(row.delivered)
  const matches = COD_CONFIG.filter((c) => {
    if (c.code !== row.cpCode) return false
    const from = new Date(c.effectiveFrom)
    const to = c.effectiveTo ? new Date(c.effectiveTo) : null
    return del >= from && (!to || del <= to)
  })
  if (matches.length)
    return matches.sort((a, b) => new Date(b.effectiveFrom) - new Date(a.effectiveFrom))[0]
  const parent = (DUMMY_CODES.find((x) => x.code === row.cpCode) || {}).parent
  const fallback = COD_CONFIG.find((c) => {
    const cc = DUMMY_CODES.find((x) => x.code === c.code)
    return cc && cc.parent === parent && c.vertical === row.vertical
  })
  return fallback || null
}
function parseCutoff(t) {
  const m = String(t || '').match(/(\d{1,2}):(\d{2})/)
  return m ? {h: +m[1], min: +m[2]} : {h: 23, min: 59}
}
export function expectedRemittance(row) {
  const cfg = configFor(row)
  if (!cfg)
    return {
      date: null,
      deriv: 'Cycle config missing for ' + row.cpCode + ' — overdue classification blocked.',
      missing: true,
    }
  const del = new Date(row.delivered)
  const co = parseCutoff(cfg.cutoff)
  const afterCutoff =
    del.getHours() > co.h || (del.getHours() === co.h && del.getMinutes() > co.min)
  const start = new Date(del.getFullYear(), del.getMonth(), del.getDate())
  start.setDate(start.getDate() + cfg.cycle)
  if (afterCutoff) start.setDate(start.getDate() + 1)
  const wkSet = {}
  ;(cfg.weekdays || []).forEach((w) => {
    wkSet[w] = 1
  })
  const probe = new Date(start)
  let guard = 0
  let found = null
  while (guard < 14) {
    const wd = WEEKDAYS[(probe.getDay() + 6) % 7]
    if (wkSet[wd]) {
      found = new Date(probe)
      break
    }
    probe.setDate(probe.getDate() + 1)
    guard++
  }
  if (!found) found = new Date(start)
  const deriv =
    'Delivered ' +
    fmtDT(row.delivered) +
    '; cycle D+' +
    cfg.cycle +
    '; ' +
    (afterCutoff ? 'after' : 'before') +
    ' ' +
    cfg.cutoff +
    ' cut-off; next settlement weekday [' +
    (cfg.weekdays || []).join('/') +
    '] = ' +
    fmtD(found) +
    '.'
  return {date: found, deriv, missing: false}
}

/* ───────── M6.4.3 COD Status logic ───────── */
export function activeDisputeFor(awb) {
  return DISPUTES.find((d) => d.awb === awb && d.status !== 'Resolved' && d.status !== 'Rejected')
}
export function codStatus(row) {
  if (row._reversed) return 'Reversed'
  if (activeDisputeFor(row.awb)) return 'Disputed'
  if (row.courierStatus === 'Received') return 'Received'
  const er = expectedRemittance(row)
  if (er.missing) return 'Due'
  return TODAY > er.date ? 'Overdue' : 'Due'
}
export function overdueBucket(row) {
  const er = expectedRemittance(row)
  if (er.missing || row.courierStatus === 'Received') return null
  const days = Math.floor((TODAY - er.date) / 86400000)
  if (days < 0) return 'notdue'
  if (days <= 3) return '0-3'
  if (days <= 7) return '4-7'
  if (days <= 15) return '8-15'
  return '15+'
}

/* ───────── visibility helpers ───────── */
export const visibleCodRows = () => {
  const v = visibleVerticals()
  return COD_ROWS.filter((r) => v.indexOf(r.vertical) >= 0)
}
export const visibleDisputes = () => {
  const v = visibleVerticals()
  return DISPUTES.filter((d) => v.indexOf(disputeVertical(d)) >= 0)
}
export const visibleConfig = () => {
  const v = visibleVerticals()
  return COD_CONFIG.filter((c) => v.indexOf(c.vertical) >= 0)
}

/* ───────── disputes lifecycle helpers ───────── */
export const disputeStep = (status) => {
  const i = DISPUTE_LIFECYCLE.indexOf(status)
  return i < 0 ? 0 : i
}
export const disputeBadgeKey = (status) =>
  ({
    Open: 'open',
    'Sent to Ops': 'underreview',
    'Status Corrected': 'confirmed',
    'Reversal Generated': 'reversed',
    Resolved: 'resolved',
    Rejected: 'rejected',
  })[status] || 'open'
export const nextStatus = (status) => {
  const i = DISPUTE_LIFECYCLE.indexOf(status)
  if (i < 0 || i >= 3) return null
  return DISPUTE_LIFECYCLE[i + 1]
}
export function transition(d, status, reason) {
  d.history = d.history || []
  d.history.push({status, by: CURRENT_USER, at: TODAY.toISOString(), reason: reason || ''})
  d.status = status
  d.lastUpdate = TODAY.toISOString().slice(0, 10)
}

/* COD Remittance POC for a courier, from partner onboarding */
export function podPocFor(courier) {
  const p = (typeof getPartner === 'function' && findPartnerByName(courier)) || null
  if (!p) return null
  return (p.pocs || []).find((c) => (c.roles || []).some((r) => r.indexOf('COD') >= 0)) || null
}
function findPartnerByName(name) {
  const code = DUMMY_CODES.find((c) => c.parent === name)
  if (!code) return null
  return getPartner(code.parentId)
}

/* SLA prefill for the cycle config editor (onboarding COD SLA) */
export function slaFor(code) {
  const c = DUMMY_CODES.find((x) => x.code === code)
  if (!c) return null
  const p = getPartner(c.parentId)
  if (!p) return null
  return c.vertical === 'PTL' ? p.codPTL : p.codD2C
}

/* small date formatters mirroring PS.fmt (kept local to avoid circulars) */
function fmtD(d) {
  if (!d) return '—'
  return new Date(d).toLocaleDateString('en-GB', {day: '2-digit', month: 'short', year: 'numeric'})
}
function fmtDT(d) {
  if (!d) return '—'
  const x = new Date(d)
  return (
    x.toLocaleDateString('en-GB', {day: '2-digit', month: 'short', year: 'numeric'}) +
    ', ' +
    x.toLocaleTimeString('en-GB', {hour: '2-digit', minute: '2-digit'})
  )
}

/* dispute factory used by Workspace + Upload */
export function makeDispute({row, mismatch, status, owner, note, autoSystem}) {
  return {
    awb: row.awb,
    courier: row.courier,
    cpCode: row.cpCode,
    codValue: row.codValue,
    proshipStatus: 'Delivered',
    courierStatus: row.courierStatus,
    mismatch,
    status: status || 'Open',
    owner: owner || 'ops@prozo.com',
    age: 0,
    lastUpdate: TODAY.toISOString().slice(0, 10),
    note,
    history: [
      {
        status: 'Open',
        by: autoSystem ? 'system' : CURRENT_USER,
        at: TODAY.toISOString(),
        reason: note,
      },
      {
        status: 'Sent to Ops',
        by: 'system',
        at: TODAY.toISOString(),
        reason: autoSystem
          ? 'Auto-created from remittance upload exception'
          : 'Structured verification task auto-created for ' + (owner || 'ops@prozo.com'),
      },
    ],
  }
}
